import { CSSProperties, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { generatePath, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { backButton, miniApp } from '@telegram-apps/sdk-react';

import KeepSwipingButton from './Components/KeepSwipingButton.js';

import LottieAnimation from '@/animations/LottieAnimation.tsx';
import firework_lottie from '@/components/common/svg/assets/firework.json';
import { CandidateUI } from '@/api/matchmaker/types.ts';
import OpenChatButton from '@/components/Discovery/MatchedPopup/Components/OpenChatButton.tsx';
import UIConfig from '@/UIConfig.ts';
import { routes } from '@/navigation/routes.tsx';
import { ImageWithLoader } from '@/components/common/Image/ImageWithLoader.tsx';
import HeartSimpleShape from '@/components/common/svg/shapes/HeartSimpleShape.tsx';
import { useBackButtonToCloseModal } from '@/hooks/useBackButtonToCloseModal.tsx';
import { ServerPhotoV2 } from '@/shared/type/userDataFields.ts';
import { helpers } from '@/helpers.ts';

interface IProps {
  handleClose: () => void;
  avatar?: ServerPhotoV2;
  candidate: CandidateUI | null;
}

const MatchedPopup = ({ handleClose, avatar, candidate }: IProps) => {
  useBackButtonToCloseModal(handleClose, true);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isAnimated, setIsAnimated] = useState(false);
  const [appBottomBarColor] = useState(miniApp.bottomBarColor());

  const handleKeepSwipingClick = () => {
    if (backButton.isVisible()) {
      backButton.hide();
    }

    handleClose();
  };

  useEffect(() => {
    miniApp.setBottomBarColor('#1D1D1D');
    const timer = setTimeout(() => setIsAnimated(true), 100);

    return () => {
      miniApp.setBottomBarColor(appBottomBarColor);
      clearTimeout(timer);
    };
  }, []);

  return (
    <div style={containerStyle}>
      {/* Фейерверк */}
      <div style={fireworkContainerStyle}>
        <LottieAnimation
          width={300}
          height={300}
          animationData={firework_lottie}
          shouldAnimate={isAnimated}
          loop={false}
        />
      </div>

      <div style={centerContainerStyle}>
        <div style={photoContainerStyle}>
          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: -60 }}
            animate={isAnimated ? { opacity: 1, scale: 1, x: -60 } : {}}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 40,
              duration: 0.8
            }}
            style={animatedPhotoStyle}
          >
            <ImageWithLoader
              fullWidth={false}
              style={photoCandidateStyle}
              src={
                helpers.getPrimaryPhoto(
                  candidate?.profile.v2photos?.[0]?.variants ?? []
                ) ?? ''
              }
              thumbhash={helpers.getThumbHash(
                candidate?.profile.v2photos?.[0]?.variants ?? []
              )}
              alt=""
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: 60 }}
            animate={isAnimated ? { opacity: 1, scale: 1, x: 60 } : {}}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 40,
              duration: 0.8,
              delay: 0.1
            }}
            style={{ ...animatedPhotoStyle, zIndex: 5 }}
          >
            <ImageWithLoader
              fullWidth={false}
              style={photoUserStyle}
              src={helpers.getPrimaryPhoto(avatar?.variants ?? [])}
              thumbhash={helpers.getThumbHash(avatar?.variants ?? [])}
              alt=""
            />
          </motion.div>
        </div>

        <div style={textContainerStyle}>
          <p style={textStyle}>{t('chats.new_match')}</p>
          <p style={subTextStyle}>
            {t('matched_popup.match_subtext', {
              name: candidate?.profile.name
            })}
          </p>
          <div style={{ marginBottom: -20, marginRight: -150 }}>
            <HeartSimpleShape
              height={80}
              width={80}
              fillColor="#ffffff"
              style={{ transform: 'rotate(-26deg)', opacity: 0.02 }}
            />
          </div>
        </div>
      </div>
      <div style={{ position: 'absolute', bottom: -75, right: -75 }}>
        <HeartSimpleShape
          height={300}
          width={300}
          fillColor="#ffffff"
          style={{ transform: 'rotate(-26deg)', opacity: 0.02 }}
        />
      </div>
      <div style={footerContainerStyle}>
        <OpenChatButton
          onClick={() =>
            navigate(generatePath(routes.chat, { id: candidate?.profile.hid }))
          }
        />
        <KeepSwipingButton handleClose={handleKeepSwipingClick} />
      </div>
    </div>
  );
};

const containerStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'relative',
  background: UIConfig.Colors.BackgroundDark,
  overflow: 'hidden'
};

const fireworkContainerStyle: CSSProperties = {
  position: 'absolute',
  top: '5%',
  zIndex: 1
};

const centerContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  zIndex: 2,
  marginBottom: '30%'
};

const photoContainerStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '180px',
  width: '100%',
  zIndex: 2,
  marginBottom: '10px'
};

const animatedPhotoStyle: CSSProperties = {
  position: 'absolute'
};

const photoCandidateStyle: CSSProperties = {
  width: '120px',
  height: '120px',
  objectFit: 'cover',
  borderRadius: '50%',
  boxShadow: '0 0 12px rgba(0, 0, 0, 0.3)'
};

const photoUserStyle: CSSProperties = {
  width: '140px',
  height: '140px',
  objectFit: 'cover',
  borderRadius: '50%',
  border: `12px solid ${UIConfig.Colors.BackgroundDark}`
};

const textContainerStyle: CSSProperties = {
  width: '70%',
  marginTop: '5px',
  textAlign: 'center'
};

const textStyle: CSSProperties = {
  fontSize: '30px',
  color: '#fff',
  margin: 0,
  fontWeight: 700,
  fontFamily: UIConfig.Fonts.Primary
};

const subTextStyle: CSSProperties = {
  fontSize: '14px',
  color: '#BCBCBC',
  marginTop: '10px'
};

const footerContainerStyle: CSSProperties = {
  width: '100%',
  position: 'absolute',
  bottom: '120px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '5px',
  zIndex: 3
};

export default MatchedPopup;
